// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ApiParameter, post, ServiceResult } from "@insite/client-framework/Services/ApiService";
import { CartModel } from "@insite/client-framework/Types/ApiModels";

const customFormBasicUrl = "/api/v1/awcrfq";

// Main Rfq From Service
export interface MainRfqApiParameter extends ApiParameter {
    FirstName: string;
    LastName: string;
    Email: string;
    Company: string;
    Phone: string;
    CountryCode: string;
    State: string;
    City: string;
    PostalCode: string;
    Products: string;
    AttachmentBase64?: string;
    AttachmentFileName?: string;
    AttachmentMimeType?: string;
    formUrl?: string; 
    utmParameters?: { [key: string]: string };
}

export interface MainRfqModal {}

export async function SubmitMainRfqFormService(parameter: MainRfqApiParameter): Promise<ServiceResult<MainRfqModal>> {
    try {
        const result = await post<MainRfqModal>(`${customFormBasicUrl}/submitMainRfq`, parameter as MainRfqModal);
        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

// Quick Rfq From Service
export interface QuickRfqApiParameter extends Omit<MainRfqApiParameter, "Products"> {
    Notes: string;
    ErpNumber: string;
    Length: string;
    Color?: string;
    StreetAddress: string;
    IsExpedited?: boolean;
    RequiredBy?: string;
}

export interface QuickRfqModal {}

export async function SubmitQuickRfqFormService(
    parameter: QuickRfqApiParameter,
): Promise<ServiceResult<QuickRfqModal>> {
    try {
        const result = await post<QuickRfqModal>(`${customFormBasicUrl}/submitPdpRfq`, parameter as QuickRfqModal);
        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

// Cart Rfq From Service
export interface CartRfqApiParameter extends ApiParameter {
    firstname: string;
    lastName: string;
    company: string;
    phone: string;
    email: string;
    countryCode: string;
    state: string;
    city: string;
    postalCode: string;
    notes: string;
    cart: CartModel;
    attachmentBase64?: string;
    attachmentFileName?: string;
    attachmentMimeType?: string;
    onlyQuoteRequired?: boolean;
}

export interface CartRfqModal {}

export async function SubmitCartRfqFormService(parameter: CartRfqApiParameter): Promise<ServiceResult<CartRfqModal>> {
    try {
        const result = await post<CartRfqModal>(`${customFormBasicUrl}/SubmitCartRfq`, parameter as CartRfqModal);
        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

// Contact-Us From Service
export interface ContactUsApiParameter extends Omit<MainRfqApiParameter, "Products"> {
    Notes?: string;
}

export interface ContactUsModal {}

export async function SubmitContactUsFormService(
    parameter: ContactUsApiParameter,
): Promise<ServiceResult<ContactUsModal>> {
    try {
        const result = await post<ContactUsModal>(
            `${customFormBasicUrl}/submitContactUsForm`,
            parameter as ContactUsModal,
        );
        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        if (error.status === 403) {
            return {
                successful: false,
                errorMessage: error.errorMessage,
                statusCode: 403,
            };
        }
        throw error;
    }
}
